/**
 * Servizio per la lettura di codici a barre dalla Tessera Sanitaria
 * Utilizza html5-qrcode per una scansione più affidabile su mobile e desktop
 */

import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

// Istanza dello scanner
let html5QrCode = null;
let isScanning = false;

// Formati supportati per tessera sanitaria - TUTTI i formati
const SUPPORTED_FORMATS = [
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.CODE_93,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODABAR,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.DATA_MATRIX,
  Html5QrcodeSupportedFormats.PDF_417,
  Html5QrcodeSupportedFormats.AZTEC
];

/**
 * Verifica se il browser supporta l'accesso alla fotocamera
 */
export const isCameraSupported = () => {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
};

/**
 * Ottiene la lista delle fotocamere disponibili
 */
export const getAvailableCameras = async () => {
  try {
    const devices = await Html5Qrcode.getCameras();
    return devices;
  } catch (error) {
    console.error('Errore nel recupero delle fotocamere:', error);
    return [];
  }
};

/**
 * Inizializza lo scanner
 * @param {string} elementId - ID dell'elemento container per il video
 */
export const initScanner = (elementId) => {
  if (html5QrCode) {
    cleanup();
  }
  html5QrCode = new Html5Qrcode(elementId, {
    formatsToSupport: SUPPORTED_FORMATS,
    verbose: false
  });
  return html5QrCode;
};

/**
 * Avvia la scansione dalla fotocamera
 * @param {string} elementId - ID dell'elemento container
 * @param {function} onSuccess - Callback quando viene trovato un codice
 * @param {function} onError - Callback per errori
 * @param {object} options - Opzioni di configurazione
 */
export const startScanning = async (elementId, onSuccess, onError, options = {}) => {
  if (isScanning) {
    console.log('Scansione già in corso');
    return;
  }
  
  try {
    const scanner = initScanner(elementId);
    
    // Config ottimizzata per barcode lineari (tessera sanitaria)
    const config = {
      fps: 15, // Frame rate più alto per migliore riconoscimento
      qrbox: options.qrbox || { width: 350, height: 100 }, // Più largo per barcode lineari
      aspectRatio: options.aspectRatio || 1.777, // 16:9
      disableFlip: false,
      experimentalFeatures: {
        useBarCodeDetectorIfSupported: true // Usa API nativa se disponibile
      },
      ...options
    };
    
    // Prova prima con la fotocamera posteriore
    const cameras = await getAvailableCameras();
    let cameraId = null;
    
    if (cameras.length > 0) {
      // Cerca la fotocamera posteriore
      const backCamera = cameras.find(cam => 
        cam.label.toLowerCase().includes('back') || 
        cam.label.toLowerCase().includes('rear') ||
        cam.label.toLowerCase().includes('posteriore') ||
        cam.label.toLowerCase().includes('environment')
      );
      
      cameraId = backCamera ? backCamera.id : cameras[cameras.length - 1].id;
      console.log('Fotocamere disponibili:', cameras.map(c => c.label));
      console.log('Usando fotocamera:', cameraId);
    }
    
    await scanner.start(
      cameraId || { facingMode: "environment" },
      config,
      (decodedText, decodedResult) => {
        console.log('Codice trovato:', decodedText);
        console.log('Formato:', decodedResult.result.format?.formatName);
        onSuccess(decodedText, decodedResult);
      },
      (errorMessage) => {
        // Errori normali durante la scansione - ignora
        // Solo log in debug mode
      }
    );
    
    isScanning = true;
    console.log('Scansione avviata con config:', config);
    
  } catch (error) {
    console.error('Errore avvio scansione:', error);
    isScanning = false;
    if (onError) {
      onError(error);
    }
    throw error;
  }
};

/**
 * Ferma la scansione
 */
export const stopScanning = async () => {
  if (html5QrCode && isScanning) {
    try {
      await html5QrCode.stop();
      console.log('Scansione fermata');
    } catch (error) {
      console.log('Errore stop scansione:', error);
    }
    isScanning = false;
  }
};

/**
 * Scansiona da un file immagine
 * @param {File} imageFile - File immagine
 */
export const scanFromFile = async (elementId, imageFile) => {
  const scanner = initScanner(elementId);
  
  try {
    const result = await scanner.scanFile(imageFile, true);
    console.log('Codice da file:', result);
    return result;
  } catch (error) {
    console.log('Nessun codice trovato nel file');
    return null;
  }
};

/**
 * Estrae il codice fiscale dal valore del barcode
 * Il codice fiscale italiano ha 16 caratteri: AAABBB00A00A000A
 * - 6 lettere (cognome + nome)
 * - 2 numeri (anno)
 * - 1 lettera (mese)
 * - 2 numeri (giorno + sesso)
 * - 1 lettera + 3 numeri (codice comune)
 * - 1 lettera (controllo)
 */
export const extractCodiceFiscaleFromBarcode = (barcodeValue) => {
  if (!barcodeValue) return null;
  
  // Pulisci il valore - rimuovi spazi, trattini, e converti in maiuscolo
  let cleaned = barcodeValue
    .replace(/[\s\-\.\,\/\\]/g, '')
    .toUpperCase()
    .trim();
  
  console.log('Barcode raw:', barcodeValue);
  console.log('Barcode pulito:', cleaned, 'lunghezza:', cleaned.length);
  
  // Pattern codice fiscale italiano (più permissivo)
  // Formato: 6 lettere + 2 numeri + 1 lettera + 2 numeri + 1 lettera + 3 numeri + 1 lettera
  const cfPatternStrict = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;
  const cfPatternSearch = /[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]/g;
  
  // Se il barcode è esattamente 16 caratteri e matcha il pattern
  if (cleaned.length === 16 && cfPatternStrict.test(cleaned)) {
    console.log('✅ Codice Fiscale valido (esatto):', cleaned);
    return cleaned;
  }
  
  // Cerca il pattern CF in una stringa più lunga
  const matches = cleaned.match(cfPatternSearch);
  if (matches && matches.length > 0) {
    console.log('✅ Codice Fiscale estratto:', matches[0]);
    return matches[0];
  }
  
  // Prova a estrarre solo i primi/ultimi 16 caratteri se alfanumerici
  if (cleaned.length >= 16) {
    const first16 = cleaned.slice(0, 16);
    const last16 = cleaned.slice(-16);
    
    if (cfPatternStrict.test(first16)) {
      console.log('✅ CF dai primi 16 caratteri:', first16);
      return first16;
    }
    if (cfPatternStrict.test(last16)) {
      console.log('✅ CF dagli ultimi 16 caratteri:', last16);
      return last16;
    }
  }
  
  // Se il barcode è il numero tessera (20 cifre che inizia con 8038)
  if (/^8038[0-9]{16}$/.test(cleaned)) {
    console.log('📋 Numero tessera trovato:', cleaned);
    return { type: 'tessera', value: cleaned };
  }
  
  // Se è solo numerico (potrebbe essere numero tessera)
  if (/^[0-9]{18,20}$/.test(cleaned)) {
    console.log('📋 Numero tessera (numerico):', cleaned);
    return { type: 'tessera', value: cleaned };
  }
  
  // Se sembra un codice fiscale ma con qualche errore (es. O invece di 0)
  // Prova a correggere errori comuni OCR
  const corrected = cleaned
    .replace(/O/g, '0') // O -> 0
    .replace(/I/g, '1') // I -> 1
    .replace(/S/g, '5') // S -> 5
    .replace(/B/g, '8'); // B -> 8
  
  if (corrected.length === 16 && cfPatternStrict.test(corrected)) {
    console.log('✅ CF corretto (OCR fix):', corrected);
    return corrected;
  }
  
  // Ultimo tentativo: se è lungo 16 caratteri, potrebbe essere il CF con formato strano
  if (cleaned.length === 16 && /^[A-Z0-9]{16}$/.test(cleaned)) {
    // Verifica struttura base: inizia con lettere
    if (/^[A-Z]{3}/.test(cleaned)) {
      console.log('⚠️ Possibile CF (formato non standard):', cleaned);
      return cleaned;
    }
  }
  
  // Non riconosciuto - ritorna raw
  console.log('❌ Barcode non riconosciuto come CF, valore raw:', cleaned);
  return { type: 'unknown', value: cleaned };
};

/**
 * Rilascia le risorse
 */
export const cleanup = async () => {
  await stopScanning();
  if (html5QrCode) {
    try {
      html5QrCode.clear();
    } catch (e) {
      // Ignora errori di cleanup
    }
    html5QrCode = null;
  }
};

/**
 * Verifica se la scansione è attiva
 */
export const isScanningActive = () => isScanning;

export default {
  isCameraSupported,
  getAvailableCameras,
  initScanner,
  startScanning,
  stopScanning,
  scanFromFile,
  extractCodiceFiscaleFromBarcode,
  cleanup,
  isScanningActive
};
